'use client'

import { type PointerEvent, type ReactNode, useRef } from 'react'

import { cn } from '@/lib/utils'

/** How far the row leans at the edge of the host, in degrees. */
const MAX_DEG = 5

/**
 * A row of cards that leans toward the pointer TOGETHER.
 *
 * `TiltCard` leans one card under the cursor; this leans the whole row from
 * anywhere on the screen, so the three tiers move as one object. The host
 * publishes `--group-rx` / `--group-ry` and whatever wears
 * {@link TILT_GROUP_ITEM} reads them — written straight to `style` in rAF,
 * never through React state, for the same reason `TiltCard` gives.
 *
 * Frozen over controls, like `TiltCard`: a button that slides while you reach
 * for it eats the click. Off for `pointer: coarse` and reduced motion.
 */
export function TiltGroup({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const frame = useRef<number | null>(null)

  const live = () =>
    window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const onMove = (event: PointerEvent<HTMLDivElement>) => {
    const host = ref.current
    if (!host || !live()) return
    if ((event.target as Element | null)?.closest?.('button, a, input, select, textarea, label')) {
      return
    }
    const box = host.getBoundingClientRect()
    const px = (event.clientX - box.left) / box.width - 0.5
    const py = (event.clientY - box.top) / box.height - 0.5
    if (frame.current !== null) return
    frame.current = requestAnimationFrame(() => {
      frame.current = null
      host.style.setProperty('--group-rx', `${(-py * MAX_DEG * 2).toFixed(2)}deg`)
      host.style.setProperty('--group-ry', `${(px * MAX_DEG * 2).toFixed(2)}deg`)
    })
  }

  const reset = () => {
    const host = ref.current
    if (!host) return
    host.style.setProperty('--group-rx', '0deg')
    host.style.setProperty('--group-ry', '0deg')
  }

  return (
    <div ref={ref} onPointerMove={onMove} onPointerLeave={reset} className={className}>
      {children}
    </div>
  )
}

/** What the leaning row wears. Only from `md`: stacked on a phone there is no row. */
export const TILT_GROUP_ITEM = cn(
  'md:[transform:perspective(1600px)_rotateX(var(--group-rx,0deg))_rotateY(var(--group-ry,0deg))]',
  'md:transition-transform md:duration-200 md:ease-out motion-reduce:md:transform-none',
)
